import React, { useState, useContext, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { CartContext } from '../Context/CartContext';
import { Menu, X, ShoppingCart } from 'lucide-react';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { cart } = useContext(CartContext);
    const navRef = React.useRef();
    const location = useLocation();

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            const offset = window.scrollY;
            setScrolled(offset > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Auto-close menu on route change
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location]);

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMenuOpen && navRef.current && !navRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isMenuOpen]);

    const navItems = [
        { to: "/", text: "בית" },
        { to: "/personalCustomization", text: "הזמנה בהתאמה אישית" },
        { to: "/contactUs", text: "בואו נדבר" },
    ];

    return (
        <>
            {/* Mobile backdrop */}
            {isMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-40 md:hidden transition-opacity"
                    onClick={() => setIsMenuOpen(false)}
                />
            )}

            <nav
                ref={navRef}
                className={`
                    fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-out
                    ${scrolled
                    ? 'bg-surface shadow-soft py-1 border-b border-secondary/30'
                    : 'bg-background py-3'
                }
                `}
                dir="rtl"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 md:h-20">

                        {/* Mobile Menu Button - Right side in RTL */}
                        <div className="flex md:hidden">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMenu();
                                }}
                                className={`
                                    p-2 rounded-lg transition-all duration-300
                                    text-text-primary hover:text-primary hover:bg-primary/5 
                                    active:scale-95 group
                                    ${isMenuOpen ? 'rotate-180' : 'rotate-0'}
                                `}
                                aria-label={isMenuOpen ? 'סגור תפריט' : 'פתח תפריט'}
                                aria-expanded={isMenuOpen}
                            >
                                {isMenuOpen ? (
                                    <X className="w-6 h-6 transition-transform duration-300" />
                                ) : (
                                    <Menu className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
                                )}
                            </button>
                        </div>

                        {/* Logo - Right side in RTL (Organic Luxury Style) */}
                        <div className="flex-shrink-0">
                            <Link
                                to="/"
                                className="flex items-center gap-2.5 group"
                                aria-label="עמוד בית - פומה"
                            >
                                <span className="text-2xl md:text-3xl font-bold tracking-wide text-primary">
                                    פומה
                                </span>
                            </Link>
                        </div>

                        {/* Desktop Navigation - Center */}
                        <div className="hidden md:flex items-center justify-center flex-1">
                            <div className="flex items-center gap-8">
                                {navItems.map((item, index) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        className={({ isActive }) => `
                                            relative px-4 py-2 text-base md:text-lg font-medium tracking-wide
                                            transition-all duration-300 rounded-lg group
                                            ${isActive
                                            ? 'text-primary'
                                            : 'text-text-secondary hover:text-primary hover:bg-primary/5'
                                        }
                                            before:absolute before:bottom-0 before:right-0 before:h-0.5 
                                            before:bg-accent before:transition-all before:duration-300
                                            ${isActive ? 'before:w-full' : 'before:w-0 hover:before:w-full'}
                                        `}
                                        style={{ transitionDelay: `${index * 50}ms` }}
                                    >
                                        {item.text}
                                    </NavLink>
                                ))}
                            </div>
                        </div>

                        {/* Cart Icon - Left side in RTL */}
                        <div className="flex items-center">
                            <Link
                                to="/orders"
                                className="
                                    relative p-3 rounded-lg text-text-primary hover:text-primary
                                    hover:bg-primary/5 transition-all duration-300 group
                                    active:scale-95
                                "
                                aria-label={`עגלת קניות - ${cart.length} פריטים`}
                            >
                                <ShoppingCart className="w-6 h-6 md:w-7 md:h-7 transition-transform duration-300 group-hover:scale-110" />
                                {cart.length > 0 && (
                                    <span className="
                                        absolute top-0 right-0 bg-accent text-white
                                        text-[10px] md:text-xs font-bold rounded-full h-5 w-5 md:h-6 md:w-6
                                        flex items-center justify-center
                                        shadow-sm border-2 border-surface
                                        transition-transform duration-300 group-hover:scale-110
                                    ">
                                        {cart.length > 99 ? '99+' : cart.length}
                                    </span>
                                )}
                                {/* Notification dot for empty cart */}
                                {cart.length === 0 && (
                                    <span className="
                                        absolute top-1 right-1 w-2.5 h-2.5 bg-primary/30
                                        rounded-full opacity-0 group-hover:opacity-100
                                        transition-opacity duration-300
                                    " />
                                )}
                            </Link>
                        </div>
                    </div>

                    {/* Mobile Navigation Menu */}
                    <div className={`
                        md:hidden absolute top-full right-0 left-0
                        bg-surface shadow-soft border-b border-secondary/50 
                        transition-all duration-300 ease-out origin-top
                        ${isMenuOpen
                        ? 'opacity-100 visible scale-y-100'
                        : 'opacity-0 invisible scale-y-0'
                    }
                    `}>
                        <div className="px-4 py-6">
                            <div className="space-y-2">
                                {navItems.map((item, index) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        onClick={() => setIsMenuOpen(false)}
                                        className={({ isActive }) => `
                                            block text-right p-4 text-lg font-medium 
                                            transition-all duration-300 rounded-xl
                                            border-r-4
                                            ${isActive
                                            ? 'text-primary bg-primary/5 border-primary shadow-sm'
                                            : 'text-text-secondary hover:text-primary hover:bg-secondary/30 border-transparent hover:border-accent/50'
                                        }
                                        `}
                                        style={{
                                            transitionDelay: `${index * 50}ms`,
                                            transform: isMenuOpen ? 'translateX(0)' : 'translateX(20px)',
                                            opacity: isMenuOpen ? 1 : 0
                                        }}
                                    >
                                        {item.text}
                                    </NavLink>
                                ))}
                            </div>

                            {/* Mobile Cart Summary */}
                            <div className="mt-6 pt-6 border-t border-secondary">
                                <Link
                                    to="/orders"
                                    onClick={() => setIsMenuOpen(false)}
                                    className="
                                        flex items-center justify-between p-4
                                        bg-primary/5 rounded-xl hover:bg-primary/10
                                        transition-all duration-300 group border border-transparent hover:border-primary/20
                                    "
                                >
                                    <span className="text-text-primary group-hover:text-primary transition-colors font-medium">
                                        מעבר לעגלת הקניות
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-primary font-bold text-lg">{cart.length}</span>
                                        <ShoppingCart className="w-5 h-5 text-primary" />
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Spacer to prevent content jumping - שמרנו את הפתרון החכם שלך! */}
            <div className="h-20 md:h-24" />
        </>
    );
};

export default Navbar;